<template>
  <div>
    <Head title="AVCs" />
    <h1 class="mb-8 text-3xl font-bold">Average Variable Costs</h1>
    <div class="flex items-center justify-between mb-6">
      <search-filter v-model="form.search" class="mr-4 w-full max-w-md" @reset="reset" />
      <text-input v-model="form.date" type="date" class="w-full max-w-md" />
      <a
        v-if="user.group_permissions.includes('add_avcs')
          || user.per_user_permissions.includes('add_avcs')"
        class="btn-orange" href="#" @click="openUploadWindow"
      >
        <span>Upload</span>
        <span class="hidden md:inline">&nbsp;AVCs</span>
      </a>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-3">Upload date</th>
          <th class="pb-3 pt-3 px-3">SKUs</th>
          <th class="pb-3 pt-3 px-3">Uploaded by</th>
          <th class="pb-3 pt-3 px-3">Uploaded on</th>
          <th class="pb-3 pt-3 px-3" />
        </tr>
        <tr v-for="averageVariableCost in averageVariableCosts.data" :key="averageVariableCost.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/average-variable-cost/${averageVariableCost.id}/average-variable-cost-entries`">
              {{ averageVariableCost.date }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/average-variable-cost/${averageVariableCost.id}/average-variable-cost-entries`">
              {{ averageVariableCost.total_skus }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/average-variable-cost/${averageVariableCost.id}/average-variable-cost-entries`">
              {{ averageVariableCost.user }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/average-variable-cost/${averageVariableCost.id}/average-variable-cost-entries`">
              {{ averageVariableCost.uploaded_on }}
            </Link>
          </td>
          <td class="w-px border-t">
            <Link class="flex items-center px-3" :href="`average-variable-cost/${averageVariableCost.id}/average-variable-cost-entries`" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </Link>
          </td>
        </tr>
        <tr v-if="averageVariableCosts.data.length === 0">
          <td class="px-3 py-3 border-t" colspan="4">No data found</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="averageVariableCosts.links" />
  </div>
  <div v-if="openUpload" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white w-1/2 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex modal-wrapper">
            <h1 style="font-size: 20px;">Upload AVCs File</h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <form enctype="multipart/form-data" @submit.prevent="store">
                <div class="flex flex-wrap -mb-2 -mr-6 pl-8 pr-8 pt-8 pb-2">
                  <file-input v-model="formData.avc_file" :error="$props.errors.avc_file" class="pb-2 pr-6 w-full lg:w-full" type="file" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" label="AVCs Excel File" />
                  <p v-if="$props.errors.avc_file"><small class="text-red-700">{{ $props.errors.avc_file }}</small></p>
                </div>
                <div class="flex flex-wrap -mb-8 -mr-6 pt-2 pl-8 pr-8 pb-8">
                  <text-input v-model="formData.date" :error="$props.errors.date" type="date" class="pb-8 pr-6 w-full lg:w-full" label="Date" />
                </div>
                <div class="flex flex-wrap -mb-8 -mr-6 pt-2 pl-8 pr-8 pb-8">
                  <Checkbox label="Overwrite Existing Part numbers" :active="active" @input="overwrite = $event" />
                </div>
              </form>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-red sm:text-sm sm:leading-5" @click="store">
                Process
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                Close
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
  
<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '@/Shared/Pagination'
import FileInput from '@/Shared/FileInput'
import TextInput from '@/Shared/TextInput'
import Checkbox from '@/Shared/Checkbox'

export default {
  components: {
    Head,
    Icon,
    Link,
    SearchFilter,
    Pagination,
    FileInput,
    TextInput,
    Checkbox,
  },
  layout: Layout,
  props: {
    filters: Object,
    averageVariableCosts: Object,
    errors: Object,
  },
  data() {
    return {
      form: {
        search: this.filters.search,
        date: this.filters.date,
      },
      overwrite: false,
      formData: {
        avc_file: null,
        date: null,
      },
      openUpload: false,
    }
  },
  computed: {
    user() {
      return this.$inertia.page.props.auth.user
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/average-variable-costs', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    openUploadWindow() {
      this.openUpload = true
    },
    closeModal() {
      this.openUpload = false
      this.$props.errors.avc_file = ''
      this.$props.errors.date = ''
    },
    store() {
      this.formData.overwrite = this.overwrite
      this.$inertia.post('/average-variable-costs', this.formData, {
        onSuccess: () => {
          this.openUpload = false
        },
      })
    },
  },
}
</script>
  